// AM dashboard, budget pacing (bottom section). PURE: spine rows + master
// budgets in, per-account bars and per-currency totals out. No clock.
//
// DEFINITIONS (Scott's brief, 2026-11-08)
//  · Periods: This month (default) / Last month / Past 7 days / Past 30 days,
//    all ending at the platform's last COMPLETE loaded day (`completeDayBound`),
//    never at today's partial figures.
//  · Expected by today = the pro-rated monthly budget. This month: budget ×
//    elapsed ÷ days-in-month. Last month: the whole budget. Past 7 / 30 days:
//    budget × days ÷ days-in-month (of the month the window ends in).
//  · Pace % = spent ÷ expected. Projected month end = spent ÷ elapsed × days in
//    month, THIS MONTH ONLY.
//  · Colour: green 90-110, amber 75-90 or 110-125, red outside.
//  · An account without a budget shows "No budget set" and is EXCLUDED from the
//    totals (the count is reported). ⚠️ In the Combined tab only channels WITH a
//    budget contribute spend, so a Meta-only budget is never divided into Meta
//    plus unbudgeted Google spend.
//  · Money is never summed across currencies: totals are per currency.

import type { AdChannel, HomeAccount, SpineDay } from '../../types/advertisingHome.js';
import {
  addDaysYmd,
  dayOfMonth,
  daysInMonthOf,
  monthStartOf,
  sumWindow,
} from '../advertisingHome/spine.js';

export type PacePeriod = 'this_month' | 'last_month' | 'past_7' | 'past_30';
export type PaceTab = 'combined' | 'meta' | 'google';
export type PaceStatus = 'green' | 'amber' | 'red';

export interface PaceWindow {
  from: string;
  to: string;
  /** Days in the window. */
  days: number;
  /** Fraction of ONE monthly budget the window should have consumed. */
  expectedShare: number;
  isThisMonth: boolean;
  /** Days elapsed in the month, this month only (for the projection). */
  elapsed: number | null;
  daysInMonth: number;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** `lastDay` = the platform's last complete loaded day (YYYY-MM-DD). */
export function paceWindow(period: PacePeriod, lastDay: string): PaceWindow {
  const dim = daysInMonthOf(lastDay);
  switch (period) {
    case 'this_month': {
      const elapsed = dayOfMonth(lastDay);
      return {
        from: monthStartOf(lastDay),
        to: lastDay,
        days: elapsed,
        expectedShare: elapsed / dim,
        isThisMonth: true,
        elapsed,
        daysInMonth: dim,
      };
    }
    case 'last_month': {
      const lastOfPrev = addDaysYmd(monthStartOf(lastDay), -1);
      const prevDim = daysInMonthOf(lastOfPrev);
      return {
        from: monthStartOf(lastOfPrev),
        to: lastOfPrev,
        days: prevDim,
        expectedShare: 1,
        isThisMonth: false,
        elapsed: null,
        daysInMonth: prevDim,
      };
    }
    case 'past_7':
    case 'past_30': {
      const days = period === 'past_7' ? 7 : 30;
      return {
        from: addDaysYmd(lastDay, -(days - 1)),
        to: lastDay,
        days,
        expectedShare: days / dim,
        isThisMonth: false,
        elapsed: null,
        daysInMonth: dim,
      };
    }
  }
}

/** green 90-110, amber 75-90 or 110-125, red outside. `pace` is a ratio (1 = on pace). */
export function paceStatus(pace: number): PaceStatus {
  if (pace >= 0.9 && pace <= 1.1) return 'green';
  if ((pace >= 0.75 && pace < 0.9) || (pace > 1.1 && pace <= 1.25)) return 'amber';
  return 'red';
}

export interface PaceRow {
  accountId: string;
  name: string;
  currency: string;
  /** Monthly budget in native units (cents / 100), summed over the channels in the tab. */
  budget: number;
  spent: number;
  expected: number;
  /** spent ÷ expected; null when expected is 0. */
  pace: number | null;
  /** Projected month end, THIS MONTH ONLY (null otherwise). */
  projected: number | null;
  status: PaceStatus | null;
}

export interface PaceTotal {
  currency: string;
  budget: number;
  spent: number;
  expected: number;
  pace: number | null;
  projected: number | null;
  status: PaceStatus | null;
}

export interface PaceResult {
  window: PaceWindow;
  /** Budgeted accounts, worst pace first (furthest from 100%). */
  rows: PaceRow[];
  /** Accounts with no budget for this tab: "No budget set", excluded from totals. */
  noBudget: Array<{ accountId: string; name: string }>;
  totals: PaceTotal[];
}

const budgetOf = (a: HomeAccount, ch: AdChannel): number | null => {
  const cents = ch === 'meta' ? a.metaMonthlyBudgetCents : a.googleMonthlyBudgetCents;
  return cents !== null && cents > 0 ? cents / 100 : null;
};

const channelsFor = (tab: PaceTab): AdChannel[] =>
  tab === 'combined' ? ['meta', 'google'] : [tab];

export function buildPace(input: {
  accounts: readonly HomeAccount[];
  rows: readonly SpineDay[];
  period: PacePeriod;
  tab: PaceTab;
  /** Last complete loaded day per channel (null = nothing loaded). */
  lastDay: { meta: string | null; google: string | null };
}): PaceResult {
  const { period, tab } = input;
  // One window for the page: the OLDER of the loaded channels' last days, so a
  // lagging platform never reads as underspend against a day it has not loaded.
  const days = channelsFor(tab)
    .map((c) => input.lastDay[c])
    .filter((d): d is string => d !== null);
  const lastDay = days.length > 0 ? days.reduce((a, b) => (a < b ? a : b)) : null;
  const empty = (w: PaceWindow): PaceResult => ({ window: w, rows: [], noBudget: [], totals: [] });
  if (lastDay === null) return empty(paceWindow(period, '2000-01-01'));
  const window = paceWindow(period, lastDay);

  const byAccountChannel = new Map<string, SpineDay[]>();
  for (const r of input.rows) {
    const k = `${r.accountId}|${r.channel}`;
    const list = byAccountChannel.get(k);
    if (list) list.push(r);
    else byAccountChannel.set(k, [r]);
  }

  const rows: PaceRow[] = [];
  const noBudget: PaceResult['noBudget'] = [];
  for (const a of input.accounts) {
    const budgeted = channelsFor(tab).filter(
      (c) => (c === 'meta' ? a.runsMeta : a.runsGoogle) && budgetOf(a, c) !== null,
    );
    if (budgeted.length === 0) {
      noBudget.push({ accountId: a.id, name: a.name });
      continue;
    }
    let budget = 0;
    let spent = 0;
    for (const c of budgeted) {
      budget += budgetOf(a, c) ?? 0;
      const w = sumWindow(byAccountChannel.get(`${a.id}|${c}`) ?? [], window.from, window.to);
      // The account's own currency; a row in another currency is not blended in.
      spent += w.spendByCurrency.find((x) => x.currency === a.currency)?.amount ?? 0;
    }
    const expected = budget * window.expectedShare;
    const pace = expected > 0 ? spent / expected : null;
    rows.push({
      accountId: a.id,
      name: a.name,
      currency: a.currency,
      budget: round2(budget),
      spent: round2(spent),
      expected: round2(expected),
      pace,
      projected:
        window.isThisMonth && window.elapsed
          ? round2((spent / window.elapsed) * window.daysInMonth)
          : null,
      status: pace === null ? null : paceStatus(pace),
    });
  }
  rows.sort(
    (x, y) =>
      Math.abs((y.pace ?? 1) - 1) - Math.abs((x.pace ?? 1) - 1) || x.name.localeCompare(y.name),
  );

  const totals = new Map<string, PaceTotal>();
  for (const r of rows) {
    const t = totals.get(r.currency) ?? {
      currency: r.currency,
      budget: 0,
      spent: 0,
      expected: 0,
      pace: null,
      projected: window.isThisMonth ? 0 : null,
      status: null,
    };
    t.budget += r.budget;
    t.spent += r.spent;
    t.expected += r.expected;
    if (t.projected !== null && r.projected !== null) t.projected += r.projected;
    totals.set(r.currency, t);
  }
  const totalList = [...totals.values()]
    .map((t) => {
      const pace = t.expected > 0 ? t.spent / t.expected : null;
      return {
        ...t,
        budget: round2(t.budget),
        spent: round2(t.spent),
        expected: round2(t.expected),
        projected: t.projected === null ? null : round2(t.projected),
        pace,
        status: pace === null ? null : paceStatus(pace),
      };
    })
    .sort((a, b) => a.currency.localeCompare(b.currency));

  return { window, rows, noBudget, totals: totalList };
}
